import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Input, Select, DatePicker, Tooltip, Button, Popconfirm, message, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  Search, Users, Calendar, Phone, Play, MessageSquareText, PhoneCall, PhoneIncoming, PhoneMissed, Timer,
  BellRing, CheckCheck, UserPlus, Headset, RotateCcw,
} from 'lucide-react';
import Layout from '../../Layout';
import {
  agentsOf, callCenterApi, callRows, customerByPhone, filterCalls, fmtDuration, followUps, hasRecording, kpis,
  rangeBounds, statusCounts, useCallCenter,
  type CallLog, type CallRow, type RangePreset, type StatusFilter,
} from './callCenterStore';
import { softphone } from './softphone';
import Softphone from './components/Softphone';
import CreateCustomerModal from './components/CreateCustomerModal';
import { TaskDoneButton } from './components/TaskWidgets';
import { AgentStatusDropdown, KindBadge, QuickNotesModal, RecordingModal, UnknownBadge } from './components/CallWidgets';
import { useCskhDemoAutoload } from '../CskhHub/demoData';

const STATUS_CHIPS: { key: StatusFilter; label: string; Icon?: React.ComponentType<{ size?: number | string }>; cls: string; active: string }[] = [
  { key: 'all', label: 'Tất cả cuộc gọi', cls: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50', active: 'bg-gray-800 text-white border-gray-800' },
  { key: 'pending', label: 'Chưa xử lý', Icon: BellRing, cls: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100', active: 'bg-rose-600 text-white border-rose-600' },
  { key: 'out', label: 'Gọi đi', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', active: 'bg-emerald-600 text-white border-emerald-600' },
  { key: 'in', label: 'Gọi đến', cls: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100', active: 'bg-blue-600 text-white border-blue-600' },
  { key: 'missed_in', label: 'Gọi đến lỡ', cls: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', active: 'bg-red-600 text-white border-red-600' },
  { key: 'missed_out', label: 'Gọi đi lỡ', cls: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100', active: 'bg-orange-500 text-white border-orange-500' },
  { key: 'unknown', label: 'Số máy lạ', cls: 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200', active: 'bg-gray-600 text-white border-gray-600' },
];

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'all', label: 'Tất cả thời gian' },
  { value: 'today', label: 'Hôm nay' },
  { value: '7d', label: '7 ngày qua' },
  { value: '30d', label: '30 ngày qua' },
  { value: 'custom', label: 'Tuỳ chọn ngày…' },
];

const ago = (t: number, now = Date.now()) => {
  const m = Math.floor((now - t) / 60000);
  if (m < 1) return 'vừa xong';
  if (m < 60) return `${m} phút trước`;
  if (dayjs(t).isSame(now, 'day')) return `${Math.floor(m / 60)} giờ trước`;
  if (dayjs(t).isSame(dayjs(now).subtract(1, 'day'), 'day')) return 'hôm qua';
  return `${dayjs(now).startOf('day').diff(dayjs(t).startOf('day'), 'day')} ngày trước`;
};

function Kpi({ icon, label, value, hint, tone }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string; tone: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex items-center gap-3 shadow-sm">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">{value}</div>
        {hint && <div className="text-[11px] text-gray-400 truncate">{hint}</div>}
      </div>
    </div>
  );
}

export default function CallCenterPage() {
  useCskhDemoAutoload();
  const s = useCallCenter();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [recording, setRecording] = useState<CallLog | null>(null);
  const [notesPhone, setNotesPhone] = useState<string | null>(null);
  const [createPhone, setCreatePhone] = useState<string | null>(null);
  const [showAllFollow, setShowAllFollow] = useState(false);

  const q = params.get('q') || '';
  const agent = params.get('agent');
  const range = (params.get('range') as RangePreset) || 'all';
  const from = params.get('from');
  const to = params.get('to');
  const status = (params.get('status') as StatusFilter) || 'all';

  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const now = Date.now();
  const rows = useMemo(() => callRows(s), [s]);
  const filter = { q, agent, range: rangeBounds(range, from, to, now), status };
  const list = filterCalls(rows, filter);
  const counts = statusCounts(rows, filter);
  const stats = kpis(s, now);
  const follow = followUps(s, now);
  const agentOptions = useMemo(() => agentsOf(s).map(a => ({ value: a, label: a })), [s]);
  const hasFilter = !!(q || agent || range !== 'all' || status !== 'all');

  const openCustomer = (phone: string) => {
    const c = customerByPhone(s, phone);
    navigate(c ? `/customer-demo/detail?id=${c.id}` : `/customer-demo/detail?phone=${encodeURIComponent(phone)}`);
  };

  const columns: ColumnsType<CallRow> = [
    {
      title: 'KHÁCH HÀNG', key: 'customer', width: 230, fixed: 'left',
      render: (_, r) => (
        <div className="flex items-center gap-3 py-1">
          <div className={`w-9 h-9 rounded-full ${r.customer?.color || 'bg-gray-400'} text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0`}>
            {r.customer ? r.customer.name.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="min-w-0">
            <button type="button" onClick={() => openCustomer(r.phone)} className="font-semibold text-gray-900 dark:text-gray-100 hover:text-[#3e91ab] cursor-pointer text-left truncate max-w-[160px] block">
              {r.customer?.name || 'Số máy lạ'}
            </button>
            <div className="text-[13px] text-blue-500 font-medium">{r.phone}</div>
            {r.customer?.doNotCall && <span className="text-[10px] font-bold text-red-600 uppercase" title={r.customer.doNotCall.reason}>⛔ Chặn gọi</span>}
            {!r.customer && (
              <button type="button" onClick={() => setCreatePhone(r.phone)} className="text-[11px] text-[#3e91ab] hover:underline cursor-pointer flex items-center gap-1">
                <UserPlus size={11} /> Tạo khách hàng
              </button>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'THỜI GIAN', key: 'time', width: 130,
      render: (_, r) => (
        <div>
          <div className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{dayjs(r.startedAt).format('HH:mm DD/MM')}</div>
          <div className="text-[11px] text-gray-400">{ago(r.startedAt, now)}</div>
        </div>
      ),
    },
    {
      title: 'CUỘC GỌI', key: 'kind', width: 150,
      render: (_, r) => (
        <div className="flex flex-col gap-1">
          <KindBadge kind={r.kind} />
          {r.pending && <span className="text-[10px] font-bold text-rose-600 uppercase tracking-wide">● Chưa xử lý</span>}
          {!r.customer && !r.pending && <UnknownBadge />}
        </div>
      ),
    },
    {
      title: 'THỜI LƯỢNG', key: 'duration', width: 100, align: 'center',
      render: (_, r) => <span className="text-[13px] tabular-nums text-gray-700 dark:text-gray-300">{r.duration ? fmtDuration(r.duration) : '—'}</span>,
    },
    {
      title: 'NỘI DUNG', key: 'note', width: 260,
      render: (_, r) => (r.note
        ? <Tooltip title={r.note}><span className="text-[13px] text-gray-600 dark:text-gray-300 line-clamp-2">{r.note}</span></Tooltip>
        : <span className="text-[13px] text-gray-300">—</span>),
    },
    {
      title: 'NHÂN VIÊN', key: 'agent', width: 160,
      render: (_, r) => (
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-[9px] shrink-0">{r.agent.charAt(0).toUpperCase()}</div>
          <span className="text-[13px] text-gray-700 dark:text-gray-300">{r.agent}</span>
        </div>
      ),
    },
    {
      title: 'CHI NHÁNH / DỊCH VỤ', key: 'service', width: 170,
      render: (_, r) => (r.customer ? (
        <div className="flex flex-col gap-1 items-start">
          <span className="text-[13px] text-gray-700 dark:text-gray-300">{r.customer.branch || '—'}</span>
          {r.customer.service
            ? <span className="px-2 py-0.5 bg-pink-50 text-pink-600 rounded text-xs font-medium border border-pink-100">{r.customer.service}</span>
            : <span className="px-2 py-0.5 bg-gray-50 text-gray-400 rounded text-xs border border-gray-200">Chưa có dịch vụ</span>}
        </div>
      ) : <span className="text-[13px] text-gray-300">—</span>),
    },
    {
      title: 'TƯƠNG TÁC', key: 'action', width: 150, align: 'center', fixed: 'right',
      render: (_, r) => {
        const rec = hasRecording(r);
        return (
          <div className="flex items-center justify-center gap-1.5">
            <button type="button" onClick={() => softphone.dial(r.phone)} className={`w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer ${r.customer?.doNotCall ? 'opacity-30' : ''} ${r.pending ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-green-50 text-green-600 hover:bg-green-100'}`} title={r.pending ? 'Gọi lại' : 'Gọi điện'}>
              <Phone size={13} />
            </button>
            <button type="button" disabled={!rec} onClick={() => setRecording(r)} className={`w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center transition ${rec ? 'hover:bg-blue-100 hover:text-blue-600 text-gray-500 cursor-pointer' : 'opacity-30 cursor-not-allowed text-gray-400'}`} title={rec ? 'Nghe ghi âm' : 'Không có ghi âm'}>
              <Play size={13} />
            </button>
            <button type="button" onClick={() => setNotesPhone(r.phone)} className="w-7 h-7 rounded-full bg-gray-50 text-gray-500 flex items-center justify-center hover:bg-blue-100 hover:text-blue-600 transition cursor-pointer" title="Ghi chú">
              <MessageSquareText size={13} />
            </button>
            {r.pending && (
              <Popconfirm title="Đánh dấu đã xử lý cuộc gọi nhỡ này?" okText="Đã xử lý" cancelText="Huỷ" onConfirm={() => { callCenterApi.markHandled(r.id); message.success('Đã đánh dấu xử lý'); }}>
                <button type="button" className="w-7 h-7 rounded-full bg-gray-50 text-gray-500 flex items-center justify-center hover:bg-emerald-100 hover:text-emerald-600 transition cursor-pointer" title="Đánh dấu đã xử lý">
                  <CheckCheck size={13} />
                </button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  const followShown = showAllFollow ? follow : follow.slice(0, 4);

  return (
    <Layout>
      <Helmet><title>Tổng đài | Khothi</title></Helmet>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Tổng đài (Call center)</h1>
            <p className="text-xs text-gray-500">Nhật ký cuộc gọi, gọi lại khách nhỡ và ghi kết quả chăm sóc</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AgentStatusDropdown />
            <Tooltip title="Demo: giả lập một khách gọi vào tổng đài">
              <Button icon={<PhoneIncoming size={14} />} onClick={() => softphone.simulateIncoming()}>Mô phỏng gọi đến</Button>
            </Tooltip>
            <Button type="primary" icon={<Headset size={14} />} onClick={() => softphone.openDialer()}>Gọi số mới</Button>
          </div>
        </div>

        {/* KPI hôm nay */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <Kpi icon={<PhoneCall size={18} />} tone="bg-blue-50 text-blue-600" label="Cuộc gọi hôm nay" value={stats.total} hint={`${stats.incoming} gọi đến · ${stats.outgoing} gọi đi`} />
          <Kpi icon={<PhoneMissed size={18} />} tone="bg-red-50 text-red-600" label="Tỉ lệ nhỡ hôm nay" value={`${stats.missedRate}%`} hint={`${stats.missed} cuộc không kết nối`} />
          <Kpi icon={<Timer size={18} />} tone="bg-emerald-50 text-emerald-600" label="Thời lượng TB" value={fmtDuration(stats.avgDuration)} hint="Trên các cuộc nghe máy" />
          <Kpi icon={<BellRing size={18} />} tone="bg-amber-50 text-amber-600" label="Cần gọi lại" value={stats.followUps} hint="Nhỡ chưa xử lý + hẹn tới hạn" />
        </div>

        {/* Việc cần gọi lại */}
        {follow.length > 0 && (
          <div className="bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-900 rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-amber-100 dark:border-amber-900 flex items-center justify-between">
              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 flex items-center gap-2"><BellRing size={15} className="text-amber-500" /> Cần gọi lại ({follow.length})</div>
              {follow.length > 4 && <button type="button" onClick={() => setShowAllFollow(v => !v)} className="text-xs text-[#3e91ab] hover:underline cursor-pointer">{showAllFollow ? 'Thu gọn' : 'Xem tất cả'}</button>}
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {followShown.map(f => {
                const c = customerByPhone(s, f.phone);
                return (
                  <div key={f.key} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => openCustomer(f.phone)} className="font-semibold text-gray-900 dark:text-gray-100 hover:text-[#3e91ab] cursor-pointer">{c?.name || 'Số máy lạ'}</button>
                        <span className="text-blue-500 text-[13px]">{f.phone}</span>
                        {f.type === 'missed'
                          ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-100">Gọi nhỡ {ago(f.at, now)}</span>
                          : <span className={`text-[11px] px-1.5 py-0.5 rounded border ${f.overdue ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                            {f.overdue ? 'Quá hẹn' : 'Hẹn'} {dayjs(f.at).format(dayjs(f.at).isSame(now, 'day') ? 'HH:mm' : 'HH:mm DD/MM')}
                          </span>}
                      </div>
                      {f.note && <div className="text-xs text-gray-500 truncate">{f.note}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="small" type="primary" icon={<Phone size={12} />} onClick={() => softphone.dial(f.phone, { callbackId: f.type === 'callback' ? f.refId : null })}>Gọi lại</Button>
                      {f.type === 'missed'
                        ? <Button size="small" icon={<CheckCheck size={12} />} onClick={() => { callCenterApi.markHandled(f.refId); message.success('Đã xử lý'); }}>Xong</Button>
                        : (() => { const cb = s.callbacks.find(x => x.id === f.refId); return cb ? <TaskDoneButton cb={cb} /> : null; })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bộ lọc */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm tên, số điện thoại"
              value={q} onChange={e => go({ q: e.target.value || null })} />
            <Select allowClear showSearch optionFilterProp="label" className="w-full sm:w-[220px]" placeholder="Nhân viên phụ trách"
              suffixIcon={<Users size={14} />} options={agentOptions} value={agent || undefined} onChange={v => go({ agent: v || null })} />
            <Select className="w-full sm:w-[180px]" suffixIcon={<Calendar size={14} />} options={RANGE_OPTIONS} value={range}
              onChange={v => go({ range: v === 'all' ? null : v, ...(v !== 'custom' ? { from: null, to: null } : {}) })} />
            {range === 'custom' && (
              <DatePicker.RangePicker format="DD/MM/YYYY" value={from && to ? [dayjs(from), dayjs(to)] : null}
                onChange={v => go({ from: v?.[0]?.format('YYYY-MM-DD') || null, to: v?.[1]?.format('YYYY-MM-DD') || null })} />
            )}
            {hasFilter && (
              <button type="button" onClick={() => setParams(new URLSearchParams(), { replace: true })} className="text-xs text-gray-500 hover:text-[#3e91ab] flex items-center gap-1 cursor-pointer">
                <RotateCcw size={12} /> Xoá bộ lọc
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {STATUS_CHIPS.map(({ key, label, Icon, cls, active }) => (
              <button key={key} type="button" onClick={() => go({ status: key === 'all' ? null : key })}
                className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border shrink-0 flex items-center gap-1.5 cursor-pointer transition ${status === key ? active : cls}`}>
                {Icon && <Icon size={13} />} {label}
                <span className={`text-[11px] px-1.5 rounded-full ${status === key ? 'bg-white/25' : 'bg-black/5'}`}>{counts[key]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Nhật ký cuộc gọi */}
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          size="middle"
          scroll={{ x: 'max-content' }}
          rowClassName={r => (r.pending ? 'bg-rose-50/40 dark:bg-rose-950/10' : '')}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={hasFilter ? 'Không có cuộc gọi khớp bộ lọc' : 'Chưa có cuộc gọi nào'} /> }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total, r) => `Hiển thị ${r[0]}–${r[1]} / ${total} cuộc gọi`,
          }}
        />
      </div>

      <Softphone />
      <RecordingModal call={recording} onClose={() => setRecording(null)} />
      <QuickNotesModal phone={notesPhone} onClose={() => setNotesPhone(null)} onOpenDetail={() => { const p = notesPhone; setNotesPhone(null); if (p) openCustomer(p); }} />
      <CreateCustomerModal phone={createPhone} onClose={() => setCreatePhone(null)} onCreated={id => navigate(`/customer-demo/detail?id=${id}`)} />
    </Layout>
  );
}
